import React, { useId } from 'react';
import i18n from 'i18next';
import { CharacterCounter } from './CharacterCounter';
import { ErrorContent } from './ErrorContent';
import { HelpContent } from './HelpContent';
import * as Styled from './Textarea.styled';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Common error message format for when value is over the character limit.
 */
export const overCharacterLimitMessage = (name, maxCharacters) => i18n.t('Textarea.overCharacterLimitMessage', {
  ns: 'blocks.textarea',
  name,
  maxCharacters,
  defaultValue: 'The {{name}} must not exceed {{maxCharacters}} characters'
});

/**
 * The `Textarea` atom, a multi-line plain-text editing control, uses the native textarea HTML
 * element with modifications to allow users to enter substantial free-form text, such as comments
 * on a review or feedback form.
 *
 * ### Uncontrolled Usage
 * Unsupported
 *
 * ### Controlled Usage
 * ```tsx
 * function ControlledTextareaExample() {
 *   const [value, setValue] = React.useState('')
 *   const [errorMessage, setErrorMessage] = React.useState('')
 *   return (
 *     <Textarea
 *       value={value}
 *       onChange={(e) => setValue(e.target.value)}
 *       onCharactersOverLimit={(isOverLimit) ? setErrorMessage('Too big!') : setErrorMessage('')}
 *       errorContent={errorMessage}
 *     />
 *     <button onClick={value !== '' ? persist(value) : setErrorMessage('Enter a value')}>Save</button>
 *   )
 * }
 * ```
 */
export const Textarea = ({
  errorContent = '',
  helpContent = '',
  placeholderContent,
  maxCharacters = 500,
  onCharactersOverLimit,
  ref,
  ...rest
}) => {
  const {
    value,
    ...other
  } = rest;
  const errorContentId = useId();
  const characterCountId = useId();

  // Narrow TS from string|number|string[]|undefined to string
  const stringVal = typeof value === 'string' && value.length ? value : '';

  // Cache the result so that we don't trigger onCharactersOverLimit on every keypress
  const isCharactersOverLimit = React.useMemo(() => stringVal.length > maxCharacters, [stringVal, maxCharacters]);
  React.useEffect(() => {
    if (onCharactersOverLimit) onCharactersOverLimit(isCharactersOverLimit);
  }, [isCharactersOverLimit, onCharactersOverLimit]);
  const hasError = errorContent !== '';
  return /*#__PURE__*/_jsxs(Styled.TextareaContainer, {
    children: [/*#__PURE__*/_jsx(Styled.Textarea, {
      placeholder: placeholderContent,
      hasError: hasError,
      value: stringVal,
      "aria-describedby": characterCountId,
      "aria-invalid": hasError ? 'true' : 'false',
      "aria-errormessage": hasError ? errorContentId : undefined,
      ...other,
      ref: ref
    }), /*#__PURE__*/_jsxs(Styled.TextareaFooter, {
      hasError: hasError,
      children: [hasError && /*#__PURE__*/_jsx(ErrorContent, {
        id: errorContentId,
        errorContent: errorContent
      }), helpContent && !hasError && /*#__PURE__*/_jsx(HelpContent, {
        helpContent: helpContent
      }), /*#__PURE__*/_jsx(CharacterCounter, {
        id: characterCountId,
        textLength: stringVal.length,
        maxCharactersAllowed: maxCharacters
      })]
    })]
  });
};